import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from '@/store';
import router from '@/router';
import withMsalAuth from '@/hocs/withMsalAuth';

/**
 * QueryClient configuration for React Query
 * - staleTime: Data considered fresh for 5 minutes
 * - cacheTime: Unused data kept in cache for 10 minutes
 * - retry: Retry failed requests up to 2 times
 * - refetchOnWindowFocus: Refetch data when window regains focus
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * AppProviders Component
 *
 * Root-level provider component that wraps the application with essential contexts:
 * - PersistGate: Delays rendering until Redux persisted state is rehydrated from localStorage
 * - QueryClientProvider: Provides React Query context for data fetching and caching
 * - RouterProvider: Provides React Router v6 routing context
 * - withMsalAuth HOC: Wraps with MSAL authentication and SosAuthProvider context
 *
 * @return {JSX.Element} Application with all providers
 */
const AppProviders = () => {
  return (
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </PersistGate>
  );
};

export default withMsalAuth(AppProviders);
